import Phaser from 'phaser';

interface ChartPoint {
  tick: number;
  value: number; // coins in hand + total banked
}

interface ChartMarker {
  tick: number;
  value: number;
  type: 'BUY' | 'SELL';
}

/** Crypto-style price chart for coin history — renders with Phaser Graphics */
export class CoinChart {
  private points: ChartPoint[] = [];
  private markers: ChartMarker[] = [];
  private died: boolean = false;

  /** Record a datapoint (call every few ticks) */
  addPoint(tick: number, coinsInHand: number, totalBanked: number): void {
    this.points.push({ tick, value: coinsInHand + totalBanked });
  }

  /** Mark spawn as BUY */
  markBuy(tick: number): void {
    this.markers.push({ tick, value: 0, type: 'BUY' });
  }

  /** Mark extraction as SELL */
  markSell(tick: number, totalValue: number): void {
    this.markers.push({ tick, value: totalValue, type: 'SELL' });
  }

  /** Mark death — line crashes to 0 */
  markDeath(tick: number): void {
    this.died = true;
    // Add crash point
    const lastValue = this.points.length > 0 ? this.points[this.points.length - 1].value : 0;
    if (lastValue > 0) {
      this.points.push({ tick, value: 0 });
    }
    this.markers.push({ tick, value: 0, type: 'SELL' });
  }

  /** Render the chart into a Phaser scene */
  render(
    scene: Phaser.Scene,
    x: number, y: number,
    width: number, height: number,
    depth: number = 300,
  ): Phaser.GameObjects.Container {
    const container = scene.add.container(x, y).setDepth(depth).setScrollFactor(0);

    // Background
    const bg = scene.add.rectangle(0, 0, width, height, 0x0a0a1a, 0.9)
      .setStrokeStyle(1, 0x333344);
    container.add(bg);

    if (this.points.length < 2) {
      const noData = scene.add.text(0, 0, 'NO DATA', {
        fontFamily: 'monospace', fontSize: '12px', color: '#444455',
      }).setOrigin(0.5);
      container.add(noData);
      return container;
    }

    // Chart area with padding
    const pad = { top: 22, bottom: 20, left: 12, right: 12 };
    const chartW = width - pad.left - pad.right;
    const chartH = height - pad.top - pad.bottom;
    const chartLeft = -width / 2 + pad.left;
    const chartTop = -height / 2 + pad.top;

    // Data range
    const minTick = this.points[0].tick;
    const maxTick = this.points[this.points.length - 1].tick;
    const tickRange = Math.max(maxTick - minTick, 1);
    const maxValue = Math.max(...this.points.map(p => p.value), 1);

    // Helper: data → screen coords
    const toX = (tick: number) => chartLeft + ((tick - minTick) / tickRange) * chartW;
    const toY = (value: number) => chartTop + chartH - (value / maxValue) * chartH;

    // Title
    const titleText = this.died ? '$SNAKE — REKT' : '$SNAKE — PROFIT';
    const titleColor = this.died ? '#ff4444' : '#00ff88';
    const title = scene.add.text(0, -height / 2 + 10, titleText, {
      fontFamily: 'monospace', fontSize: '11px', color: titleColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    // Grid lines (horizontal, subtle)
    const gridGfx = scene.add.graphics();
    gridGfx.lineStyle(1, 0x1a1a2e, 0.5);
    for (let i = 0; i <= 4; i++) {
      const gy = chartTop + (chartH * i) / 4;
      gridGfx.moveTo(chartLeft, gy);
      gridGfx.lineTo(chartLeft + chartW, gy);
    }
    gridGfx.strokePath();
    container.add(gridGfx);

    // Price line
    const lineGfx = scene.add.graphics();
    const lineColor = this.died ? 0xff4444 : 0x00ff88;

    // Draw filled area under the line
    const fillGfx = scene.add.graphics();
    fillGfx.fillStyle(lineColor, 0.08);
    fillGfx.beginPath();
    fillGfx.moveTo(toX(this.points[0].tick), chartTop + chartH);
    for (const p of this.points) {
      fillGfx.lineTo(toX(p.tick), toY(p.value));
    }
    fillGfx.lineTo(toX(this.points[this.points.length - 1].tick), chartTop + chartH);
    fillGfx.closePath();
    fillGfx.fillPath();
    container.add(fillGfx);

    // Draw the line itself
    lineGfx.lineStyle(2, lineColor, 1);
    lineGfx.beginPath();
    lineGfx.moveTo(toX(this.points[0].tick), toY(this.points[0].value));
    for (let i = 1; i < this.points.length; i++) {
      lineGfx.lineTo(toX(this.points[i].tick), toY(this.points[i].value));
    }
    lineGfx.strokePath();
    container.add(lineGfx);

    // Markers (BUY / SELL)
    for (const marker of this.markers) {
      const mx = toX(marker.tick);
      const my = toY(marker.value);
      const isBuy = marker.type === 'BUY';
      const color = isBuy ? 0x00ff88 : (this.died && marker === this.markers[this.markers.length - 1]) ? 0xff4444 : 0xffd700;
      const label = isBuy ? 'BUY' : 'SELL';

      // Dot
      const dot = scene.add.circle(mx, my, 4, color);
      container.add(dot);

      // Label
      const labelY = isBuy ? my + 10 : my - 10;
      const text = scene.add.text(mx, labelY, label, {
        fontFamily: 'monospace', fontSize: '8px',
        color: `#${color.toString(16).padStart(6, '0')}`,
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(text);

      // Value under SELL markers
      if (!isBuy && marker.value > 0) {
        const valText = scene.add.text(mx, labelY - 10, `+${marker.value}`, {
          fontFamily: 'monospace', fontSize: '7px', color: '#ffd700',
        }).setOrigin(0.5);
        container.add(valText);
      }
    }

    // Bottom axis: show total time
    const totalSeconds = Math.round(tickRange / 10); // 10 TPS
    const timeLabel = scene.add.text(chartLeft + chartW, chartTop + chartH + 10,
      `${totalSeconds}s`, {
        fontFamily: 'monospace', fontSize: '8px', color: '#444455',
      }).setOrigin(1, 0);
    container.add(timeLabel);

    // Max value label
    const maxLabel = scene.add.text(chartLeft - 2, chartTop, `${maxValue}`, {
      fontFamily: 'monospace', fontSize: '8px', color: '#444455',
    }).setOrigin(1, 0);
    container.add(maxLabel);

    return container;
  }
}
